import { useState, useRef, useCallback } from 'react';
import { X, Heart, Star, SlidersHorizontal, Plus, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui';
import { cn } from '@/lib/utils';
import { useTranslation } from '@parentpath/i18n';
import type { BabyName, SwipeDirection } from '../types';
import { NameCard } from './NameCard';

interface SwipeableStackProps {
  currentName: BabyName | null;
  /** Naechster Name, liegt als Karte hinter der aktiven */
  nextName?: BabyName | null;
  onSwipe: (nameId: string, type: 'like' | 'dislike' | 'favorite') => void;
  hasMoreNames: boolean;
  /**
   * Der Vorrat im State ist leer, aber der Server hat noch Namen. Ohne
   * diese Unterscheidung wuerde waehrend des Nachladens kurz "Keine Namen
   * mehr" aufblitzen.
   */
  isLoadingMore?: boolean;
  /** Tageslimit erreicht: die Karte darf sich gar nicht erst bewegen */
  isLimitReached?: boolean;
  /** Wird statt eines Swipes aufgerufen, wenn das Limit erreicht ist */
  onLimitReached?: () => void;
  onOpenFilters?: () => void;
  onSuggestName?: () => void;
}

const SWIPE_THRESHOLD = 100;
const ROTATION_FACTOR = 0.1;

export function SwipeableStack({
  currentName,
  nextName = null,
  onSwipe,
  hasMoreNames,
  isLoadingMore = false,
  isLimitReached = false,
  onLimitReached,
  onOpenFilters,
  onSuggestName,
}: SwipeableStackProps) {
  const { t } = useTranslation('names');
  const [dragState, setDragState] = useState({
    isDragging: false,
    startX: 0,
    startY: 0,
    currentX: 0,
    currentY: 0,
  });
  const [isAnimating, setIsAnimating] = useState(false);
  const [exitDirection, setExitDirection] = useState<SwipeDirection | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  // Die Exit-Richtung muss das Zuruecksetzen von exitDirection ueberdauern:
  // die Animation ist nach 300 ms durch, der Insert laeuft aber noch. Ohne
  // das wuerde die alte Karte in dieser Zeit zurueck in die Mitte fliegen -
  // genau der Eindruck, dass die weggeschobene Karte wiederkommt.
  const [handoffDirection, setHandoffDirection] = useState<SwipeDirection | null>(null);

  // Beim Kartenwechsel synchron zuruecksetzen statt im Effect: sonst wuerde
  // die neue Karte fuer einen Frame an der Exit-Position haengen und von
  // dort seitlich einfliegen.
  const [renderedId, setRenderedId] = useState<string | null>(currentName?.id ?? null);
  if ((currentName?.id ?? null) !== renderedId) {
    setRenderedId(currentName?.id ?? null);
    setHandoffDirection(null);
    setExitDirection(null);
  }

  // Waehrend des Uebergangs gueltige Richtung
  const activeExit = exitDirection ?? handoffDirection;

  const handleStart = useCallback((clientX: number, clientY: number) => {
    if (isAnimating) return;

    // Bei erreichtem Limit gar nicht erst ins Ziehen gehen: sonst laeuft
    // die Exit-Animation, die Karte kehrt zurueck und erst danach kommt
    // das Modal - das wirkt wie ein Fehler.
    if (isLimitReached) {
      onLimitReached?.();
      return;
    }

    setDragState({
      isDragging: true,
      startX: clientX,
      startY: clientY,
      currentX: 0,
      currentY: 0,
    });
  }, [isAnimating, isLimitReached, onLimitReached]);

  const handleMove = useCallback((clientX: number, clientY: number) => {
    if (!dragState.isDragging || isAnimating) return;
    setDragState((prev) => ({
      ...prev,
      currentX: clientX - prev.startX,
      currentY: clientY - prev.startY,
    }));
  }, [dragState.isDragging, isAnimating]);

  const handleEnd = useCallback(() => {
    if (!dragState.isDragging || isAnimating || !currentName) return;

    const { currentX, currentY } = dragState;

    // Determine swipe direction
    let direction: SwipeDirection | null = null;
    let swipeType: 'like' | 'dislike' | 'favorite' | null = null;

    if (Math.abs(currentX) > SWIPE_THRESHOLD) {
      if (currentX > 0) {
        direction = 'right';
        swipeType = 'like';
      } else {
        direction = 'left';
        swipeType = 'dislike';
      }
    } else if (currentY < -SWIPE_THRESHOLD) {
      direction = 'up';
      swipeType = 'favorite';
    }

    if (direction && swipeType) {
      // Animate exit
      setExitDirection(direction);
      setHandoffDirection(direction);
      setIsAnimating(true);

      // Wait for animation, then trigger swipe
      setTimeout(() => {
        onSwipe(currentName.id, swipeType!);
        setExitDirection(null);
        setIsAnimating(false);
        setDragState({
          isDragging: false,
          startX: 0,
          startY: 0,
          currentX: 0,
          currentY: 0,
        });
      }, 300);
    } else {
      // Snap back
      setDragState({
        isDragging: false,
        startX: 0,
        startY: 0,
        currentX: 0,
        currentY: 0,
      });
    }
  }, [dragState, isAnimating, currentName, onSwipe]);

  // Touch handlers
  const handleTouchStart = (e: React.TouchEvent) => {
    handleStart(e.touches[0].clientX, e.touches[0].clientY);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    handleMove(e.touches[0].clientX, e.touches[0].clientY);
  };

  const handleTouchEnd = () => {
    handleEnd();
  };

  // Mouse handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    handleStart(e.clientX, e.clientY);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    handleMove(e.clientX, e.clientY);
  };

  const handleMouseUp = () => {
    handleEnd();
  };

  const handleMouseLeave = () => {
    if (dragState.isDragging) {
      handleEnd();
    }
  };

  // Button handlers
  const handleButtonSwipe = (type: 'like' | 'dislike' | 'favorite') => {
    if (!currentName || isAnimating) return;

    // Vor der Animation abfangen, nicht danach
    if (isLimitReached) {
      onLimitReached?.();
      return;
    }

    const direction: SwipeDirection =
      type === 'dislike' ? 'left' :
      type === 'favorite' ? 'up' : 'right';

    setExitDirection(direction);
    setHandoffDirection(direction);
    setIsAnimating(true);

    setTimeout(() => {
      onSwipe(currentName.id, type);
      setExitDirection(null);
      setIsAnimating(false);
    }, 300);
  };

  // Calculate card transform
  const getCardStyle = (): React.CSSProperties => {
    if (activeExit) {
      const exitX = activeExit === 'left' ? -500 : activeExit === 'right' ? 500 : 0;
      const exitY = activeExit === 'up' ? -500 : 0;
      const rotation = activeExit === 'up' ? 0 : exitX * ROTATION_FACTOR;

      return {
        transform: `translate(${exitX}px, ${exitY}px) rotate(${rotation}deg)`,
        transition: 'transform 0.3s ease-out',
        opacity: 0,
      };
    }

    const { currentX, currentY, isDragging } = dragState;
    const rotation = currentX * ROTATION_FACTOR;

    return {
      transform: `translate(${currentX}px, ${currentY}px) rotate(${rotation}deg)`,
      transition: isDragging ? 'none' : 'transform 0.3s ease-out',
    };
  };

  // Hintergrundkarte: im Ruhezustand unsichtbar, waehrend des Swipes
  // waechst sie zentriert auf die Groesse der aktiven Karte. Beide Karten
  // liegen in derselben Grid-Zelle, die Box ist also in jeder Phase gleich.
  // Kein translate - die Bewegung kommt allein aus scale.
  // Nur transform und opacity, damit nichts neu layoutet wird.
  const getBackCardStyle = (): React.CSSProperties => ({
    transformOrigin: 'center center',
    transform: activeExit ? 'scale(1)' : 'scale(0.4)',
    opacity: activeExit ? 1 : 0,
    transition: 'transform 0.3s ease-out, opacity 0.3s ease-out',
    willChange: 'transform, opacity',
  });

  // Swipe indicator opacity
  const getIndicatorOpacity = (type: 'like' | 'dislike' | 'favorite') => {
    const { currentX, currentY } = dragState;
    const threshold = SWIPE_THRESHOLD * 0.5;

    if (type === 'like') return Math.min(1, Math.max(0, currentX / threshold));
    if (type === 'dislike') return Math.min(1, Math.max(0, -currentX / threshold));
    if (type === 'favorite') return Math.min(1, Math.max(0, -currentY / threshold));
    return 0;
  };

  // Nachladen laeuft noch: Ladezustand statt "Keine Namen mehr"
  if ((!hasMoreNames || !currentName) && isLoadingMore) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center px-6 pt-16 gap-3">
        <Loader2 className="w-8 h-8 animate-spin text-white" />
        <p className="text-white/70 text-sm">{t('swipe.loadingMore')}</p>
      </div>
    );
  }

  if (!hasMoreNames || !currentName) {
    return (
      <div className="flex-1 flex items-center justify-center px-6 pt-16">
        <div className="text-center p-6 max-w-sm glass-effect rounded-2xl">
          <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Heart className="w-10 h-10 text-white/60" />
          </div>
          <h3 className="text-xl font-semibold text-white mb-2">
            {t('swipe.noMoreNames')}
          </h3>
          <p className="text-white/70 mb-6">
            {t('swipe.noMoreDescription')}
          </p>
          <div className="flex flex-col gap-3">
            {onOpenFilters && (
              <Button
                variant="secondary"
                onClick={onOpenFilters}
                className="w-full bg-white/20 text-white border-white/25 hover:bg-white/30"
              >
                <SlidersHorizontal className="w-4 h-4 mr-2" />
                {t('swipe.adjustFilters')}
              </Button>
            )}
            {onSuggestName && (
              <Button
                onClick={onSuggestName}
                className="w-full bg-pp-orange hover:bg-pp-orange/90"
              >
                <Plus className="w-4 h-4 mr-2" />
                {t('swipe.suggestName')}
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col">
      {/* Card Stack Section */}
      <section className="px-6 pt-16">
        <div className="name-card-stack">
          {/* Background glass cards */}
          <div className="stack-card-bg stack-card-bg-1"></div>
          <div className="stack-card-bg stack-card-bg-2"></div>

          {/* Swipe Area */}
          {/* Grid mit genau einer Zelle: beide Karten liegen als Grid-Items
              uebereinander und werden damit identisch bemessen. Vorher lag
              die nachrueckende Karte absolut und wechselte beim Uebernehmen
              der aktiven Rolle den Layoutmodus - genau daher der Sprung. */}
          <div
            ref={cardRef}
            className="relative grid grid-cols-1"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
          >
            {/* Hintergrundkarte - liegt als Grid-Item in derselben Zelle
                wie die aktive Karte und ist dadurch in jeder Phase gleich
                bemessen. Nicht anfassbar. */}
            {nextName && (
              <NameCard
                key={nextName.id}
                name={nextName}
                className="col-start-1 row-start-1 z-0 pointer-events-none"
                style={getBackCardStyle()}
              />
            )}

            {/* Swipe Indicators */}
            <div
              className="absolute inset-0 flex items-center justify-start pl-8 pointer-events-none z-20"
              style={{ opacity: getIndicatorOpacity('dislike') }}
            >
              <div className="w-20 h-20 rounded-full bg-red-500 flex items-center justify-center">
                <X className="w-10 h-10 text-white" />
              </div>
            </div>
            <div
              className="absolute inset-0 flex items-center justify-end pr-8 pointer-events-none z-20"
              style={{ opacity: getIndicatorOpacity('like') }}
            >
              <div className="w-20 h-20 rounded-full bg-green-500 flex items-center justify-center">
                <Heart className="w-10 h-10 text-white" />
              </div>
            </div>
            <div
              className="absolute inset-0 flex items-start justify-center pt-8 pointer-events-none z-20"
              style={{ opacity: getIndicatorOpacity('favorite') }}
            >
              <div className="w-20 h-20 rounded-full bg-amber-500 flex items-center justify-center">
                <Star className="w-10 h-10 text-white" />
              </div>
            </div>

            {/* Aktive Karte, gleiche Grid-Zelle. Der key sorgt fuer ein
                Remount beim Wechsel, damit die neue Karte nicht von der
                Exit-Position her eingeflogen kommt. Sie liegt ueber der
                Hintergrundkarte, weil .name-card bereits z-index 10 setzt
                (bei Grid-Items wirkt z-index auch ohne position). */}
            <NameCard
              key={currentName.id}
              name={currentName}
              className="col-start-1 row-start-1"
              style={getCardStyle()}
            />
          </div>

          {/* Action Buttons
              Bei erreichtem Limit bleiben die Buttons klickbar, sehen aber
              deaktiviert aus: der Klick oeffnet die Paywall und erklaert,
              warum nichts passiert. */}
          <div className="name-action-buttons">
            <button
              onClick={() => handleButtonSwipe('dislike')}
              disabled={isAnimating}
              aria-disabled={isLimitReached}
              className={cn(
                'name-action-btn dislike',
                'disabled:opacity-50',
                isLimitReached && 'opacity-50'
              )}
            >
              <X className="w-7 h-7" />
            </button>
            <button
              onClick={() => handleButtonSwipe('favorite')}
              disabled={isAnimating}
              aria-disabled={isLimitReached}
              className={cn(
                'name-action-btn favorite',
                'disabled:opacity-50',
                isLimitReached && 'opacity-50'
              )}
            >
              <Star className="w-7 h-7" />
            </button>
            <button
              onClick={() => handleButtonSwipe('like')}
              disabled={isAnimating}
              aria-disabled={isLimitReached}
              className={cn(
                'name-action-btn like',
                'disabled:opacity-50',
                isLimitReached && 'opacity-50'
              )}
            >
              <Heart className="w-7 h-7" />
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
